import React from 'react';
import { makeStyles, IconButton, useTheme, Box, Typography } from '@material-ui/core';
import CalendarDesktop, { CALENDAR_TABLE_WIDTH } from '../../calendar/CalendarDesktop';
import CalendarMobile from '../../calendar/CalendarMobile';
import PopoverTrigger from '../Popover/PopoverTrigger';
import CalendarIconsInfo from '../../calendar/CalendarIconsInfo';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { ReactComponent as DownloadIcon } from '../../../resources/images/download.svg';
import { ReactComponent as PrintIcon } from '../../../resources/images/printer.svg';
import { SizeSelector } from './SizeSelector';
import { CalendarProgressDesktop, CalendarProgressMobile } from '../../calendar/CalendarProgress';
import { styleBreakMobile } from './Globals';

// Printable/Snappable view of Calendar

const useStyles = makeStyles(theme => ({
    popoverPaper: {
        borderRadius: theme.spacing(2),
        borderTopRightRadius: theme.spacing(0),
    },
    stageLabel: {
        fontSize: theme.spacing(2.5),
        fontWeight: 600,
        [styleBreakMobile(theme)]: {
            fontSize: theme.spacing(2),
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
    },
    calendarControls: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
        minWidth: CALENDAR_TABLE_WIDTH,
        marginTop: `-${theme.spacing(2.5)}px`,
        [styleBreakMobile(theme)]: {
            width: '100%',
        },
    },
    pdfButton: {
        '& *': {
            stroke: theme.palette.primary.dark,
        },
    },
    moreInfoButton: {
        marginLeft: theme.spacing(1),
    },
}));

export const MobileStageLabel = ({ stage }) => {
    const classes = useStyles();

    return (
        <div className={classes.calendarControls}>
            <Box>
                <Typography className={classes.stageLabel}>{stage}</Typography>
            </Box>
        </div>
    );
};

const CalendarLayout = ({ visits, dateOfDiagnosis, endOfTreatementDate, stage }) => {
    const classes = useStyles();
    const theme = useTheme();
    const querySelectorText = '#calendar-main-layout';

    const onDownload = async () => {
        try {
            const el = document.querySelector(querySelectorText);
            const canvas = await html2canvas(el, {
                scale: 3,
                allowTaint: false,
                backgroundColor: theme.palette.background.default,
                // NEDVC-1524: html2canvas does not render the full calendar when the viewport is too small.  Make it use the scrollable width.
                width: el.scrollWidth,
                height: el.scrollHeight,
                windowWidth: el.scrollWidth,
            });

            const dataURL = canvas.toDataURL();
            const orientation = canvas.width > canvas.height ? 'landscape' : 'portrait';
            const pdf = new jsPDF({ orientation: orientation });

            let pdfWidth = pdf.internal.pageSize.getWidth();
            let pdfHeight = pdf.internal.pageSize.getHeight();
            pdf.addImage(dataURL, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            pdf.save('calendar.pdf');
        } catch (error) {
            // TODO handle errors appropriately
            console.error(error);
        }
    };

    const onPrint = async () => {
        try {
            const canvas = await html2canvas(document.querySelector(querySelectorText), {
                scale: 3,
                allowTaint: false,
                backgroundColor: theme.palette.background.default,
            });

            const newwindow = window.open('');
            newwindow.document.body.appendChild(canvas);

            const css = '@page { size: landscape; }';
            const head = newwindow.document.head || newwindow.document.getElementsByTagName('head')[0];
            const style = newwindow.document.createElement('style');
            style.type = 'text/css';
            style.media = 'print';

            if (style.styleSheet) {
                style.styleSheet.cssText = css;
            } else {
                style.appendChild(newwindow.document.createTextNode(css));
            }

            head.appendChild(style);

            newwindow.focus();
            newwindow.print();
            window.location.reload();
        } catch (error) {
            // TODO handle errors appropriately
            console.error(error);
        }
    };

    const MobileCalendarView = <CalendarMobile visits={visits} dateOfDiagnosis={dateOfDiagnosis} endOfTreatementDate={endOfTreatementDate} />;
    const DesktopCalendarView = <CalendarDesktop visits={visits} dateOfDiagnosis={dateOfDiagnosis} endOfTreatementDate={endOfTreatementDate} />;

    const MobileCalendarControls = <MobileStageLabel stage={stage} />;

    const DesktopCalendarControls = (
        <div className={classes.calendarControls}>
            <Box>
                <Typography className={classes.stageLabel}>{stage}</Typography>
            </Box>
            <Box>
                <IconButton onClick={onDownload} aria-label="download" component="span">
                    <DownloadIcon aria-labelledby="download" className={classes.pdfButton} />
                </IconButton>

                <IconButton onClick={onPrint} aria-label="print" component="span">
                    <PrintIcon className={classes.pdfButton} aria-labelledby="print" />
                </IconButton>

                <PopoverTrigger triggerClassName={classes.moreInfoButton} buttonDefaultText="Legend" buttonActiveText="Close" className={classes.popoverPaper}>
                    <CalendarIconsInfo />
                </PopoverTrigger>
            </Box>
        </div>
    );

    const MobileCalendarProgress = <CalendarProgressMobile dateOfDiagnosis={dateOfDiagnosis} endOfTreatmentDate={endOfTreatementDate} />;
    /* TODO: replace dateOfDiagnosis and endOfTreatmentDate with actual values from backend */
    const DesktopCalendarProgress = <CalendarProgressDesktop dateOfDiagnosis={dateOfDiagnosis} endOfTreatmentDate={endOfTreatementDate} />;

    return (
        <>
            <SizeSelector mobile={MobileCalendarControls} desktop={DesktopCalendarControls} />
            <SizeSelector mobile={MobileCalendarProgress} desktop={DesktopCalendarProgress} />
            <SizeSelector mobile={MobileCalendarView} desktop={DesktopCalendarView} />
        </>
    );
};

export default CalendarLayout;
